import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

interface FormSectionProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Anchor id for in-page navigation (used by FormTemplate's section nav). */
  id: string;
  title: ReactNode;
  description?: ReactNode;
  /** Right-side action (e.g. a "Reset" or "Learn more" link). */
  action?: ReactNode;
  children: ReactNode;
}

export function FormSection({
  id,
  title,
  description,
  action,
  children,
  className,
  ...props
}: FormSectionProps) {
  return (
    <section
      id={id}
      className={cn("scroll-mt-20", className)}
      aria-labelledby={`${id}-title`}
      {...props}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="text-base font-semibold text-fg">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-fg-muted">{description}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className="rounded-[var(--radius-lg)] border border-border-subtle bg-surface">
        {children}
      </div>
    </section>
  );
}

/** Vertical stack of form rows inside a FormSection. */
export function FormFields({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("divide-y divide-border-subtle", className)}>
      {children}
    </div>
  );
}

/**
 * One labeled form row inside FormFields. Two-column layout:
 * left = label + helper, right = control. Stacks on small screens.
 */
export function FormRow({
  label,
  helper,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode;
  helper?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 px-4 py-4",
        "sm:grid sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-6 sm:items-start",
        className,
      )}
    >
      <div className="min-w-0">
        <label
          htmlFor={htmlFor}
          className="block text-sm font-medium text-fg"
        >
          {label}
        </label>
        {helper && (
          <p className="mt-1 text-xs text-fg-muted">{helper}</p>
        )}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
